import { ComplexTabList } from '@/features/ComplexTabList';
import { ContentList } from '@/features/ContentList';
import { CTA } from '@/features/CTA';
import { MarketoForm } from '@/features/MarketoForm';
import DetailContent from '@/features/shared/DetailContent';
import EmbeddedContent from '@/features/shared/EmbeddedContent';
import { withDataLayerWrapper } from '@/features/shared/HOCs';
import IconTextGrid from '@/features/shared/IconTextGrid';
import Quote from '@/features/shared/Quote';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import SolutionsSummary from '@/features/shared/SolutionsSummary';
import SquareTileCTA from '@/features/shared/SquareTileCTA';
import { Video } from '@/features/Video';
import { ColorNames, InsetTop } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useMemo } from 'react';
import { MainContentProps } from './MainContent.interface';
import useStyles from './MainContent.styles';

const mapVariantToProps = () => {
  return {};
};

const mapVariantToClassName =
  (classes: any) =>
  ({ variant, props }: any) => {
    if (/column/i.test(variant)) {
      return clsx(
        classes.columnContent,
        /bottom/.test(props.verticalAlignment) && classes.columnBottomAlign,
        /center/.test(props.verticalAlignment) && classes.columnCenterAlign,
      );
    }

    if (/^rtc$/.test(variant)) {
      return classes.content;
    }

    if (/^quote$/.test(variant)) {
      return clsx(classes.doubleSpacing);
    }

    if (/^cta$/.test(variant)) {
      return clsx(classes.doubleSpacing);
    }

    return '';
  };

const mapFirstComponentToContainerProps = ({ variant, ...props }: any) => {
  if (/^video$/.test(variant)) {
    return {
      insetTop: InsetTop.video,
    };
  }

  if (/^quote$/.test(variant)) {
    const isVertical = /^vertical$/i.test(props.orientation);

    return {
      InsetTop: isVertical ? InsetTop.quote : undefined,
    };
  }

  return {};
};

const MainContent = ({ detailContent = [] }: PropsWithChildren<MainContentProps>) => {
  const components = React.useMemo(
    () => ({
      Video: withDataLayerWrapper({ Component: Video, position: 'video' }),
      Quote: withDataLayerWrapper({ Component: Quote, position: 'quote' }),
      CaptureClicksRichText: withDataLayerWrapper({
        Component: CaptureClicksRichText,
        position: 'rich_text',
      }),
      CTA: withDataLayerWrapper({ Component: CTA, position: 'cta' }),
      SquareTileCTA: withDataLayerWrapper({
        Component: SquareTileCTA,
        position: 'square_tile_cta',
      }),
      IconTextGrid: withDataLayerWrapper({
        Component: IconTextGrid,
        position: 'icon_text_grid',
      }),
      SolutionsSummary: withDataLayerWrapper({
        Component: SolutionsSummary,
        position: 'solutions_summary',
      }),
      EmbeddedContent: withDataLayerWrapper({
        Component: EmbeddedContent,
        position: 'embedded_content',
      }),
      ContentList: withDataLayerWrapper({
        Component: ContentList,
        position: 'content_list',
      }),
      ComplexTabList: withDataLayerWrapper({
        Component: ComplexTabList,
        position: 'complex_tab_list',
      }),
      MarketoForm: withDataLayerWrapper({
        Component: MarketoForm,
        position: 'marketo_form',
      }),
    }),
    [],
  );
  const classes = useStyles({});

  const containers = useMemo(
    () =>
      detailContent.map((el, id) => {
        const {
          topSpacing,
          bottomSpacing,
          topBackgroundColor = ColorNames.eggshell,
          bottomBackgroundColor = ColorNames.eggshell,
          insetWidth,
          content,
          jumplink = false,
          anchorLabel = '',
        } = el;

        const containerProps = {
          topSpacing,
          bottomSpacing,
          topBackgroundColor,
          bottomBackgroundColor,
          ...mapFirstComponentToContainerProps(content ? content[0] || {} : {}),
        };

        return (
          content && (
            <DetailContent
              // eslint-disable-next-line react/no-array-index-key
              key={`container_${id}`}
              content={content}
              containerProps={containerProps}
              insetWidth={insetWidth}
              mapVariantToProps={mapVariantToProps}
              mapVariantToClassName={mapVariantToClassName(classes)}
              jumplink={jumplink}
              anchorLabel={anchorLabel}
              useContainer
              components={components}
            />
          )
        );
      }),
    [classes, components, detailContent],
  );

  return <>{containers}</>;
};

export default memo(MainContent);
